import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import AdultClassRegistrationAndPayment from './AdultClassRegistrationAndPayment';
import Swal from 'sweetalert2';
import { Context } from './Context'; // Adjust path to your Context file as needed

// --- STYLED COMPONENTS (Clean Light Mode Design for Standalone Course Detail Page) ---

const Container = styled.div`
  background-color: #f8fafc;
  color: #0f172a;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  min-height: 100vh;
  padding: 80px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const DetailContainer = styled.div`
  max-width: 900px;
  width: 100%;
  background: #ffffff;
  padding: 40px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const BackButton = styled.button`
  background: #f1f5f9;
  color: #0f172a;
  border: none;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  align-self: flex-start;
  transition: background 0.2s;

  &:hover {
    background: #e2e8f0;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const CourseMeta = styled.div`
  display: flex;
  justify-content: space-between;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
  flex-wrap: wrap;
  gap: 15px;
`;

const PriceTag = styled.span`
  font-size: 1.25rem;
  font-weight: 800;
  color: #0284c7;
`;

const DetailImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: cover;
  border-radius: 12px;
`;

const RegistrationForm = styled.form`
  background: #f8fafc;
  padding: 30px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 20px;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 1.4rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #334155;
  }

  input, select {
    padding: 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    background-color: #ffffff;
    transition: border-color 0.2s;

    &:focus {
      border-color: #0284c7;
    }
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    font-size: 0.9rem;
    color: #475569;
    cursor: pointer;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #0ea5e9, #4f46e5);
  color: #ffffff;
  border: none;
  padding: 16px 36px;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 10px 25px -5px rgba(14, 165, 233, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px -5px rgba(14, 165, 233, 0.5);
  }
`;

const RegisterButton = styled(CTAButton)`
  width: 100%;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

export default function TrainingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { coursesData } = useContext(Context);
  
  const [course, setCourse] = useState(null);
  
  // Form states
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [trainingMode, setTrainingMode] = useState('');
  const [agreedToPayment, setAgreedToPayment] = useState(false);

  useEffect(() => {
    // Find the course strictly from Context using the URL param `id`
    if (coursesData.length > 0 && id) {
      const foundCourse = coursesData.find((c) => String(c.id) === String(id));
      if (foundCourse) {
        setCourse(foundCourse);
      }
    }
  }, [id, coursesData]);

  const handleBack = () => {
    navigate('/trainingprograms');
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToPayment || !course) return;
    
    // Show loading indicator
    Swal.fire({
      title: 'Submitting Registration...',
      text: 'Please wait while we save your details.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await fetch('https://elexdondigitalacademy.com/api/register_course.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName,
          email,
          phone,
          courseTitle: course.title,
          courseMode: trainingMode,
          price: course.price
        })
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful!',
          text: `You have successfully registered for ${course.title} (${trainingMode}). Ensure to send in your payment and contact us.`,
          confirmButtonColor: '#3085d6'
        });

        // Reset form fields
        setFullName('');
        setEmail('');
        setPhone('');
        setTrainingMode('Online');
        setAgreedToPayment(false);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: data.error || 'Please try again.',
          confirmButtonColor: '#d33'
        });
      }
    } catch (error) {
      console.error('Submission error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'Please check your connection and try again.',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (!course) {
    return (
      <Container>
        <DetailContainer>
          <BackButton onClick={handleBack}>← Back to Programs</BackButton>
          <p>Loading course details...</p>
        </DetailContainer>
      </Container>
    );
  }

  return (
    <Container>
      <DetailContainer>
        <BackButton onClick={handleBack}>← Back to Programs</BackButton>
        
        <DetailHeader>
          <div>
            <h2 style={{ fontSize: '2.2rem', margin: '10px 0 10px 0', color: '#0f172a' }}>{course.title}</h2>
            <CourseMeta>
              <PriceTag>₦{Number(course.price).toLocaleString()}</PriceTag>
            </CourseMeta>
          </div>
          <DetailImage src={course.image} alt={course.title} />
          <p style={{ fontSize: '1.05rem', color: '#475569', lineHeight: '1.6', margin: 0 }}>
            {course.description}
          </p>
        </DetailHeader>

        <RegistrationForm onSubmit={handleRegistrationSubmit}>
          <h3>Registration</h3>
          <p>Fill and submit this form</p>
          <p>Then make the payment using any of the below methods</p>
          
          <FormGroup>
            <label>Full Name</label>
            <input 
              type="text" 
              placeholder="Enter your full name" 
              value={fullName} 
              onChange={(e) => setFullName(e.target.value)} 
              required 
            />
          </FormGroup>

          <FormGroup>
            <label>Email Address</label>
            <input 
              type="email" 
              placeholder="Enter your email address" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
          </FormGroup>

          <FormGroup>
            <label>Phone Number</label>
            <input 
              type="tel" 
              placeholder="Enter your phone number" 
              value={phone} 
              onChange={(e) => setPhone(e.target.value)} 
              required 
            />
          </FormGroup>

          <FormGroup>
            <label>Training Mode</label>
            <select 
              value={trainingMode} 
              onChange={(e) => setTrainingMode(e.target.value)} 
              required
            >
              <option value="">-- Select training mode--</option>
              <option value="Online">Online</option>
              <option value="Physical">Physical</option>
            </select>
          </FormGroup>

          <CheckboxGroup>
            <input 
              type="checkbox" 
              id="paymentAgreement" 
              checked={agreedToPayment} 
              onChange={(e) => setAgreedToPayment(e.target.checked)} 
              required 
            />
            <label htmlFor="paymentAgreement">
              I agree that I have to make payment for this training (₦{Number(course.price).toLocaleString()}) to confirm my registration.
            </label>
          </CheckboxGroup>

          <RegisterButton type="submit" disabled={!agreedToPayment}>
            Submit registration
          </RegisterButton>
        </RegistrationForm>
        
        <AdultClassRegistrationAndPayment amount={course.price} />
      </DetailContainer>
    </Container>
  );
}